import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "../../../context/AuthProvider";
import { getPracticeResult, startPractice } from "../api/projectApi";
import { PROJECT_MESSAGES } from "../messages";
import type { PracticeRequest, PracticeResult, RunOutput, StepId } from "../projectTypes";

const POLL_INTERVAL_MS = 500;
const MAX_POLL_ATTEMPTS = 60; // About 30 seconds

const EMPTY_OUTPUT: RunOutput = { text: "" };

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export interface RunOutcome {
    validated: boolean;
    firstInvalidStep?: StepId;
}

// Output text (stdout + stderr) and, for SQL, the result table
const toOutput = (result: PracticeResult): RunOutput => {
    const { stdout = "", stderr = "" } = result.execution ?? {};

    return {
        text: [stdout, stderr].filter(Boolean).join("\n"),
        table:
            Array.isArray(result.columns) && Array.isArray(result.rows)
                ? { columns: result.columns, rows: result.rows }
                : undefined
    };
};

// Sends the learner's work (a program, a web page or an SQL query) and polls until the job finishes.
// run() resolves to null when there was no usable result (error, timeout, cancelled).
export const useProjectRunner = () => {
    const { user } = useAuth();

    const [running, setRunning] = useState(false);
    const [output, setOutput] = useState<RunOutput>(EMPTY_OUTPUT);

    const abortRef = useRef<AbortController | null>(null);

    // Leaving the page stops the polling
    useEffect(() => () => abortRef.current?.abort(), []);

    const run = useCallback(
        async (request: PracticeRequest): Promise<RunOutcome | null> => {
            if (!user) {
                setOutput({ text: PROJECT_MESSAGES.loginRequired });
                return null;
            }

            // A new run replaces the one still going
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;

            setRunning(true);
            setOutput(EMPTY_OUTPUT);

            try {
                const start = await startPractice(user.id, request, controller.signal);

                if (!start.success || !start.jobId) {
                    setOutput({ text: start.message || PROJECT_MESSAGES.runError });
                    return null;
                }

                for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
                    await sleep(POLL_INTERVAL_MS);
                    if (controller.signal.aborted) return null;

                    const result = await getPracticeResult(user.id, request.kind, start.jobId, controller.signal);

                    if (result.status === "completed") {
                        setOutput(toOutput(result));
                        return { validated: !!result.validated, firstInvalidStep: result.firstInvalidStep };
                    }

                    if (result.status === "failed") {
                        setOutput({ text: result.message || PROJECT_MESSAGES.runFailed });
                        return null;
                    }
                    // waiting / active / delayed: check again
                }

                setOutput({ text: PROJECT_MESSAGES.runTimeout });
                return null;
            } catch (err) {
                if (axios.isCancel(err)) return null; // Stopped on purpose

                console.error("Project run failed:", err);
                setOutput({
                    text: axios.isAxiosError<{ message?: string }>(err)
                        ? err.response?.data?.message || PROJECT_MESSAGES.serverError
                        : PROJECT_MESSAGES.serverError
                });
                return null;
            } finally {
                if (!controller.signal.aborted) setRunning(false);
            }
        },
        [user]
    );

    return { run, running, output };
};